import React, { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import './UsageDashboard.css';

const UsageBar = ({ label, used, limit, warningThreshold = 0.8 }) => {
  const isUnlimited = limit === -1;
  const percent = isUnlimited ? 0 : limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const isWarning = !isUnlimited && limit > 0 && used / limit >= warningThreshold;
  const isExhausted = !isUnlimited && limit > 0 && used >= limit;

  return (
    <div className="usage-bar-item">
      <div className="usage-bar-header">
        <span className="usage-bar-label">{label}</span>
        <span className={`usage-bar-count ${isExhausted ? 'exhausted' : isWarning ? 'warning' : ''}`}>
          {isUnlimited ? used : `${used} / ${limit}`}
        </span>
      </div>
      {!isUnlimited && (
        <div className="usage-bar-track">
          <div
            className={`usage-bar-fill ${isExhausted ? 'exhausted' : isWarning ? 'warning' : ''}`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
      {isUnlimited && <div className="usage-bar-unlimited">Unlimited</div>}
    </div>
  );
};

const formatResetTime = (resetAt) => {
  if (!resetAt) return '';
  try {
    const reset = new Date(resetAt);
    const now = new Date();
    const diffMs = reset - now;
    if (diffMs <= 0) return 'Resets soon';
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `Resets in ${hours}h ${minutes}m`;
    return `Resets in ${minutes}m`;
  } catch {
    return '';
  }
};

const UsageDashboard = ({ subscriptionStatus, onShowHistory }) => {
  const [expanded, setExpanded] = useState(false);

  if (!subscriptionStatus) return null;

  const {
    tierName,
    staticUsed = 0,
    staticLimit = 0,
    aiUsed = 0,
    aiLimit = 0,
    webhookUsed = 0,
    webhookLimit = 0,
    canValidateStatic,
    resetAt,
  } = subscriptionStatus;

  const resetLabel = formatResetTime(resetAt);

  return (
    <div className={`usage-dashboard ${!canValidateStatic ? 'limit-reached' : ''}`}>
      <button
        className="usage-dashboard-header"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="usage-tier-badge">{tierName}</span>
        <div className="usage-dashboard-summary">
          <span className="usage-stat-compact">
            V: {staticLimit === -1 ? staticUsed : `${staticUsed}/${staticLimit}`}
          </span>
          {aiLimit !== 0 && (
            <span className="usage-stat-compact">
              AI: {aiLimit === -1 ? aiUsed : `${aiUsed}/${aiLimit}`}
            </span>
          )}
        </div>
        <div className="usage-dashboard-actions">
          {onShowHistory && (
            <span
              className="usage-history-btn"
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onShowHistory(); }}
              title="Validation history"
            >
              <HistoryIcon style={{ fontSize: 16 }} />
            </span>
          )}
          <ExpandMoreIcon
            className={`usage-expand-icon ${expanded ? 'expanded' : ''}`}
            style={{ fontSize: 18 }}
          />
        </div>
      </button>

      {expanded && (
        <div className="usage-dashboard-details">
          <UsageBar label="Validations" used={staticUsed} limit={staticLimit} />
          {aiLimit !== 0 && (
            <UsageBar label="AI Rules" used={aiUsed} limit={aiLimit} />
          )}
          {webhookLimit !== undefined && webhookLimit !== 0 && (
            <UsageBar label="Webhooks" used={webhookUsed} limit={webhookLimit} />
          )}
          {resetLabel && (
            <div className="usage-reset-label">{resetLabel}</div>
          )}
          {!canValidateStatic && (
            <div className="usage-upgrade-prompt">
              Daily limit reached. <a href="/pricing" className="usage-upgrade-link">Upgrade your plan</a> for more validations.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UsageDashboard;
